import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyTenantTheme,
  cacheTenantConfig,
  type TenantConfig,
} from '@/lib/tenant-theme';

const TenantContext = createContext<TenantConfig | null>(null);
const TenantUpdateContext = createContext<
  (changes: Partial<TenantConfig>) => void
>(() => undefined);

/**
 * Identidade do cliente cuja area de membros esta sendo servida.
 * Resolvida pelo Host no boot (ver bootstrapTenant), antes do primeiro render.
 */
export function TenantProvider({
  config,
  children,
}: {
  config: TenantConfig | null;
  children: ReactNode;
}) {
  const [current, setCurrent] = useState(config);

  const update = useCallback((changes: Partial<TenantConfig>) => {
    setCurrent((previous) => {
      if (!previous) return previous;
      const next = { ...previous, ...changes };
      applyTenantTheme(next);
      cacheTenantConfig(next);
      return next;
    });
  }, []);

  return (
    <TenantUpdateContext.Provider value={update}>
      <TenantContext.Provider value={current}>
        {children}
      </TenantContext.Provider>
    </TenantUpdateContext.Provider>
  );
}

export function useUpdateTenant(): (changes: Partial<TenantConfig>) => void {
  return useContext(TenantUpdateContext);
}

export function useTenant(): TenantConfig | null {
  return useContext(TenantContext);
}

/** Nome de exibicao, com fallback neutro (nunca a marca de outro cliente). */
export function useTenantName(): string {
  const { t } = useTranslation('common');
  return useTenant()?.name ?? t('tenant.defaultName');
}
